import { useState } from "react";
import { Check, Loader2, UserPlus, UserCheck, UserX, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import {
  useConnectionWithUser,
  useSendConnectionRequest,
  useAcceptConnection,
  useDeclineConnection,
  useRemoveConnection,
} from "@/hooks/useCommunityConnections";

export interface ConnectionButtonProps {
  targetUserId: string;
  currentUserId?: string;
  targetUserRole?: "mentor" | "entrepreneur";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  showRemoveOption?: boolean;
}

export function ConnectionButton({
  targetUserId,
  currentUserId,
  targetUserRole,
  size = "default",
  className,
  showRemoveOption = false,
}: ConnectionButtonProps) {
  const { user } = useAuth();
  const effectiveCurrentUserId = currentUserId || user?.id;

  // Fetch connection status
  const { data: connection, isLoading: isLoadingConnection } = useConnectionWithUser(targetUserId);

  // Mutations
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const sendRequest = useSendConnectionRequest();
  const acceptConnection = useAcceptConnection();
  const declineConnection = useDeclineConnection();
  const removeConnection = useRemoveConnection();

  const isLoading =
    isLoadingConnection ||
    sendRequest.isPending ||
    acceptConnection.isPending ||
    declineConnection.isPending ||
    removeConnection.isPending;

  // Can't connect with yourself
  if (effectiveCurrentUserId === targetUserId) {
    return null;
  }

  // Determine connection state
  const isSentByMe = connection?.requester_id === effectiveCurrentUserId;
  const isReceivedByMe = connection?.recipient_id === effectiveCurrentUserId;
  const status = connection?.status;

  const handleConnect = () => {
    sendRequest.mutate(targetUserId);
  };

  const handleAccept = () => {
    if (connection?.id) {
      acceptConnection.mutate({ connectionId: connection.id });
    }
  };

  const handleDecline = () => {
    if (connection?.id) {
      declineConnection.mutate(connection.id);
    }
  };

  const handleRemove = () => {
    if (connection?.id) {
      removeConnection.mutate({ connectionId: connection.id });
      setConfirmRemoveOpen(false);
    }
  };

  // Loading state
  if (isLoadingConnection) {
    return (
      <Button 
        variant="secondary" 
        size={size} 
        disabled 
        className={className}
        aria-label="Loading connection status"
        aria-busy="true"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  // No connection exists
  if (!connection) {
    const buttonText = targetUserRole === "mentor" ? "Request Mentorship" : "Connect";
    const ariaLabel = targetUserRole === "mentor" 
      ? "Send mentorship request" 
      : "Send connection request";
    
    return (
      <Button
        variant="default"
        size={size}
        onClick={handleConnect}
        disabled={isLoading}
        className={className}
        aria-label={ariaLabel}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <>
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only sm:not-sr-only sm:ml-2">{buttonText}</span>
          </>
        )}
      </Button>
    );
  }

  // Pending - I sent the request
  if (status === "pending" && isSentByMe) {
    return (
      <Button
        variant="secondary"
        size={size}
        disabled
        className={cn("cursor-not-allowed", className)}
        aria-label="Connection request pending - waiting for response"
      >
        <Clock className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only sm:not-sr-only sm:ml-2">Request Pending</span>
      </Button>
    );
  }

  // Pending - They sent the request (I need to respond)
  if (status === "pending" && isReceivedByMe) {
    return (
      <div className="flex items-center gap-2" role="group" aria-label="Connection request actions">
        <Button
          variant="default"
          size={size}
          onClick={handleAccept}
          disabled={isLoading}
          className={cn("min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0", className)}
          aria-label="Accept connection request"
        >
          {acceptConnection.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <>
              <UserCheck className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only sm:ml-2">Accept</span>
            </>
          )}
        </Button>
        <Button
          variant="outline"
          size={size}
          onClick={handleDecline}
          disabled={isLoading}
          className="min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
          aria-label="Decline connection request"
        >
          {declineConnection.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <>
              <UserX className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only sm:ml-2">Decline</span>
            </>
          )}
        </Button>
      </div>
    );
  }

  // Connected
  if (status === "accepted") {
    if (showRemoveOption) {
      return (
        <>
          <div className="flex items-center gap-2" role="group" aria-label="Connection status and actions">
            <Button
              variant="outline"
              size={size}
              className={cn(
                "bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400",
                className
              )}
              disabled
              aria-label="You are connected"
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only sm:ml-2">Connected</span>
            </Button>
            <Button
              variant="ghost"
              size={size}
              onClick={() => setConfirmRemoveOpen(true)}
              disabled={isLoading}
              className="text-muted-foreground hover:text-destructive min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
              aria-label="Remove connection"
            >
              {removeConnection.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <UserX className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>
          <AlertDialog open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove connection?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove your connection. You can reconnect at any time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRemove}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {removeConnection.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Remove Connection
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      );
    }

    return (
      <Button
        variant="outline"
        size={size}
        className={cn(
          "bg-green-50 text-green-700 border-green-200 cursor-default hover:bg-green-50 dark:bg-green-900/20 dark:text-green-400",
          className
        )}
        disabled
        aria-label="You are connected"
      >
        <Check className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only sm:not-sr-only sm:ml-2">Connected</span>
      </Button>
    );
  }

  // Declined - allow re-request
  if (status === "declined") {
    // Check if enough time has passed (e.g., 7 days)
    const declinedAt = connection.updated_at ? new Date(connection.updated_at) : null;
    const now = new Date();
    const daysSinceDeclined = declinedAt
      ? (now.getTime() - declinedAt.getTime()) / (1000 * 60 * 60 * 24)
      : Infinity;
    const canReRequest = daysSinceDeclined >= 7;

    if (isSentByMe) {
      return (
        <Button
          variant="secondary"
          size={size}
          onClick={canReRequest ? handleConnect : undefined}
          disabled={!canReRequest || isLoading}
          className={cn(
            !canReRequest && "cursor-not-allowed opacity-60",
            className
          )}
          title={!canReRequest ? "You can request again after 7 days" : undefined}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : canReRequest ? (
            <>
              <UserPlus className="h-4 w-4" />
              Connect Again
            </>
          ) : (
            "Request Declined"
          )}
        </Button>
      );
    }

    // I declined them - can accept if I change my mind
    return (
      <Button
        variant="outline"
        size={size}
        onClick={handleAccept}
        disabled={isLoading}
        className={className}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <UserCheck className="h-4 w-4" />
            Accept Request
          </>
        )}
      </Button>
    );
  }

  // Blocked - show nothing or a disabled state
  if (status === "blocked") {
    return null;
  }

  // Fallback
  return null;
}

export default ConnectionButton;
